import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { DollarSign, Plus, Trash2, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function IncomeTracker() {
  const [income, setIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    source: "",
    amount: "",
    date: new Date().toISOString().split("T")[0],
    category: "salary",
    description: "",
  });

  useEffect(() => {
    loadIncome();
  }, []);

  const loadIncome = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/income`);
      setIncome(response.data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (error) {
      console.error("Error loading income:", error);
      toast.error("Failed to load income");
    } finally {
      setLoading(false);
    }
  };

  const addIncome = async (e) => {
    e.preventDefault();
    if (!formData.source || !formData.amount) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await axios.post(`${API}/income`, {
        ...formData,
        amount: parseFloat(formData.amount),
      });
      toast.success("Income added successfully");
      setDialogOpen(false);
      setFormData({
        source: "",
        amount: "",
        date: new Date().toISOString().split("T")[0],
        category: "salary",
        description: "",
      });
      loadIncome();
    } catch (error) {
      console.error("Error adding income:", error);
      toast.error("Failed to add income");
    }
  };

  const deleteIncome = async (id) => {
    try {
      await axios.delete(`${API}/income/${id}`);
      toast.success("Income deleted");
      loadIncome();
    } catch (error) {
      console.error("Error deleting income:", error);
      toast.error("Failed to delete income");
    }
  };

  const totalIncome = income.reduce((sum, item) => sum + item.amount, 0);

  const categories = [
    { value: "salary", label: "Salary" },
    { value: "freelance", label: "Freelance" },
    { value: "business", label: "Business" },
    { value: "investment", label: "Investment" },
    { value: "rental", label: "Rental" },
    { value: "other", label: "Other" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-start"
      >
        <div>
          <h1 className="text-4xl sm:text-5xl font-heading font-light tracking-tight" data-testid="income-tracker-title">
            Income Tracker
          </h1>
          <p className="text-lg text-muted-foreground mt-2">Track all your income sources</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-income-button">
              <Plus className="w-4 h-4 mr-2" />
              Add Income
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Income</DialogTitle>
            </DialogHeader>
            <form onSubmit={addIncome} className="space-y-4">
              <div>
                <Label htmlFor="source">Source *</Label>
                <Input
                  id="source"
                  placeholder="e.g., Main Job, Freelance Project"
                  value={formData.source}
                  onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                  data-testid="income-source-input"
                />
              </div>
              <div>
                <Label htmlFor="amount">Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  data-testid="income-amount-input"
                />
              </div>
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="income-date-input"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger data-testid="income-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  placeholder="Add notes..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="income-description-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full" data-testid="submit-income-button">
                Add Income
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </motion.div>

      {/* Total Summary */}
      <Card className="bg-primary text-primary-foreground">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90 mb-2">Total Income (YTD)</p>
              <p className="text-4xl font-mono font-bold" data-testid="total-income-amount">${totalIncome.toLocaleString()}</p>
            </div>
            <DollarSign className="w-16 h-16 opacity-20" />
          </div>
        </CardContent>
      </Card>

      {/* Income List */}
      <div className="space-y-4">
        {income.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <DollarSign className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">No income recorded yet</h3>
              <p className="text-muted-foreground mb-6">Start tracking your income to get tax insights</p>
              <Button onClick={() => setDialogOpen(true)} data-testid="empty-state-add-income">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Income
              </Button>
            </CardContent>
          </Card>
        ) : (
          income.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="hover:shadow-md transition-all" data-testid={`income-item-${item.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium">{item.source}</h3>
                        <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded-full">
                          {categories.find((c) => c.value === item.category)?.label || item.category}
                        </span>
                      </div>
                      {item.description && <p className="text-sm text-muted-foreground mb-2">{item.description}</p>}
                      <div className="flex items-center text-sm text-muted-foreground">
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(item.date).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <p className="text-2xl font-mono font-semibold" data-testid={`income-amount-${item.id}`}>
                        ${item.amount.toLocaleString()}
                      </p>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteIncome(item.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-income-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}